import React from 'react';
import { useFormikContext } from 'formik';

import { derivedGroups, resolveAutoGroupBy } from '@repo/checkout';
import draftToNestedPrice from '../../lib/draftToNestedPrice';
import { Label } from '@dashboard/components/Form';
import { StarIcon as StarOutlineIcon } from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import Reorderable from '@dashboard/components/Reorderable';
import { Input } from '@dashboard/components/ui/input';
import Divider from '@/dashboard/components/Divider';

/**
 * Apply a saved tab order to a derived groups array.
 * Entries from savedOrder that still exist come first (in order), then any
 * remaining derived entries that weren't in savedOrder.
 */
export function applyTabOrder(derived, savedOrder) {
  if (!savedOrder || savedOrder.length === 0) return derived;
  const derivedSet = new Set(derived);
  const ordered = savedOrder.filter(g => derivedSet.has(g));
  const orderedSet = new Set(ordered);
  const remaining = derived.filter(g => !orderedSet.has(g));
  return [...ordered, ...remaining];
}

/**
 * Fill-missing-only reconcile for the physical tab defaults. Given the derived
 * tab set and the current stored maps, returns the maps to persist:
 *   - every derived group with NO stored label gets one = its derived name,
 *   - a missing axis default gets seeded to the first ordered tab.
 * A label stored as '' (the seller cleared it) is a deliberate value, not
 * "missing", so it's left alone. Returns null when nothing needs seeding, which
 * keeps the mount seed idempotent (a remount with nothing missing is a no-op).
 */
export function computeTabDefaultsSeed({
  axis,
  derived,
  orderedGroups,
  priceDisplayLabels,
  priceDisplayDefaults,
}) {
  const existingLabels = priceDisplayLabels?.[axis] || {};
  const seededLabels = {};
  let labelsChanged = false;
  for (const group of derived) {
    if (!(group in existingLabels)) {
      seededLabels[group] = group;
      labelsChanged = true;
    }
  }

  const hasDefault = priceDisplayDefaults?.[axis] != null;
  if (!labelsChanged && hasDefault) return null;

  const seed = {};
  if (labelsChanged) {
    seed.priceDisplayLabels = {
      ...priceDisplayLabels,
      [axis]: { ...existingLabels, ...seededLabels },
    };
  }
  if (!hasDefault) {
    seed.priceDisplayDefaults = {
      ...priceDisplayDefaults,
      [axis]: orderedGroups[0],
    };
  }
  return seed;
}

/**
 * Tabs customize sub-panel: reorder / rename / set-preselected per grouping
 * axis. Edits only touch the form state; the Settings tab's Save persists them.
 */
function TabsCustomize({ axis, orderedGroups }) {
  const { values, setFieldValue } = useFormikContext();

  const currentDefault = values.priceDisplayDefaults?.[axis] || orderedGroups[0];

  const reorderableItems = orderedGroups.map(group => ({ key: group, group }));

  function handleReorder(reordered) {
    setFieldValue('priceDisplayTabOrder', {
      ...values.priceDisplayTabOrder,
      [axis]: reordered.map(item => item.group),
    });
  }

  function handleLabelChange(group, e) {
    setFieldValue('priceDisplayLabels', {
      ...values.priceDisplayLabels,
      [axis]: {
        ...(values.priceDisplayLabels?.[axis] || {}),
        [group]: e.target.value,
      },
    });
  }

  function handleSetDefault(group) {
    setFieldValue('priceDisplayDefaults', {
      ...values.priceDisplayDefaults,
      [axis]: group,
    });
  }

  return (
    <Reorderable
      id={`tabs-reorder-${axis}`}
      items={reorderableItems}
      onReorder={handleReorder}
      renderItem={({ item, dragHandleProps }) => {
        const { group } = item;
        const label = values.priceDisplayLabels?.[axis]?.[group] ?? group;
        const isDefault = group === currentDefault;
        return (
          <div className="group flex items-center gap-2 py-1">
            <span
              {...dragHandleProps}
              className="cursor-grab text-muted-foreground/50 hover:text-muted-foreground shrink-0"
              aria-label="Drag to reorder"
            >
              <svg width="12" height="16" viewBox="0 0 12 16" fill="currentColor">
                <circle cx="4" cy="3" r="1.2" />
                <circle cx="8" cy="3" r="1.2" />
                <circle cx="4" cy="8" r="1.2" />
                <circle cx="8" cy="8" r="1.2" />
                <circle cx="4" cy="13" r="1.2" />
                <circle cx="8" cy="13" r="1.2" />
              </svg>
            </span>
            {/* Order: drag handle › star › input. Fixed-width star slot so the
                input's left edge stays aligned across rows whether the row shows
                the filled (preselected) or outline star. No text — just the icon,
                matching the price card's preselected star. */}
            <div className="flex w-6 shrink-0 items-center justify-center">
              {isDefault ? (
                <span
                  role="img"
                  aria-label={`${group} preselected`}
                  title="Preselected"
                  className="flex text-gray-700 dark:text-muted-foreground"
                >
                  <StarIcon className="size-4" aria-hidden="true" />
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => handleSetDefault(group)}
                  aria-label={`Set ${group} as preselected`}
                  title="Set as preselected"
                  className="group/star flex text-gray-400 dark:text-muted-foreground transition-colors hover:text-gray-700 dark:hover:text-muted-foreground"
                >
                  {/* Outline by default (always visible); fills on hover to
                      preview that clicking sets this row as preselected. */}
                  <StarOutlineIcon className="size-4 group-hover/star:hidden" aria-hidden="true" />
                  <StarIcon className="size-4 hidden group-hover/star:block" aria-hidden="true" />
                </button>
              )}
            </div>
            <Input
              value={label}
              placeholder={group}
              onChange={e => handleLabelChange(group, e)}
              className="h-7 text-xs flex-1 min-w-0"
              aria-label={`Label for ${group} tab`}
            />
          </div>
        );
      }}
    />
  );
}

/**
 * The grouping tabs the storefront will render for the form's prices under the
 * theme's `groupingAxis`: the axis, its derived groups and their saved order,
 * or null when grouping is off or the axis derives fewer than 2 tabs. The axis
 * itself is a theme setting - read from the page theme by the parent.
 */
export function resolvePriceTabs(values, groupingAxis) {
  // Formik price drafts are FLAT (spec §7); nest them so derivedGroups sees the
  // same shape the storefront feeds it. Hidden prices are non-existing for
  // grouping (the editor has no query-reveal), so the axis + tabs mirror the
  // default, un-revealed storefront view.
  const activePrices = (values.prices || [])
    .filter(p => p.enabled !== false && !p.hidden)
    .map(draftToNestedPrice);

  const axis =
    groupingAxis === 'billing' || groupingAxis === 'interval'
      ? groupingAxis
      : groupingAxis === 'auto'
        ? resolveAutoGroupBy(activePrices)
        : null;
  const derived = axis ? derivedGroups(activePrices, axis) : [];
  if (!axis || activePrices.length <= 1 || derived.length < 2) return null;
  return {
    axis,
    derived,
    orderedGroups: applyTabOrder(derived, values.priceDisplayTabOrder?.[axis]),
  };
}

/**
 * The tab maps to fill in when the tabs are first shown for these form values,
 * or null when nothing is missing (see computeTabDefaultsSeed).
 */
export function computeTabsSeed(values, groupingAxis) {
  const tabs = resolvePriceTabs(values, groupingAxis);
  if (!tabs) return null;
  return computeTabDefaultsSeed({
    ...tabs,
    priceDisplayLabels: values.priceDisplayLabels,
    priceDisplayDefaults: values.priceDisplayDefaults,
  });
}

/**
 * The "Price tabs" block of the prices Settings tab: rename / reorder /
 * set-preselected per grouping tab, always open. Renders nothing when grouping
 * is off or the axis derives fewer than 2 tabs.
 */
export function PriceTabsSettings({ groupingAxis }) {
  const { values } = useFormikContext();
  const tabs = resolvePriceTabs(values, groupingAxis);
  if (!tabs) return null;

  return (
    <div>
      <Divider className="mb-4" />
      <Label>Price tabs</Label>
      <p className="mb-2 text-xs text-gray-500 dark:text-muted-foreground">
        Rename and reorder your price tabs, and choose which is selected by default.
      </p>
      <TabsCustomize axis={tabs.axis} orderedGroups={tabs.orderedGroups} />
    </div>
  );
}
